/**
 * Post-reply “Used: …” receipts under assistant messages.
 */
import { useState } from 'react';
import { BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MessageReceipt } from '@/lib/workspace/types';

interface MessageReceiptsProps {
  receipts: MessageReceipt[];
  className?: string;
}

function formatReceipt(r: MessageReceipt): string {
  if (r.count != null && r.count > 0) {
    return `${r.label} (${r.count})`;
  }
  return r.label;
}

export function MessageReceipts({ receipts, className }: MessageReceiptsProps) {
  const [open, setOpen] = useState(false);
  if (!receipts?.length) return null;

  // Prefer project-ish layers in the collapsed summary
  const priority = new Set([
    'memory',
    'context',
    'project_instructions',
    'grounded',
    'persona',
    'nostr_pack',
    'nostr_mention',
    'nostr_auto',
    'attachment',
  ]);
  const highlight = receipts.filter((r) => priority.has(r.kind));
  const summaryList = (highlight.length ? highlight : receipts).slice(0, 4);
  const summary = summaryList.map(formatReceipt).join(' · ');
  const more = receipts.length - summaryList.length;

  return (
    <div className={cn('mt-2', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 max-w-full text-[11px] text-[#5a5a5a] hover:text-[#8a8884] transition-colors text-left"
        title="Sources injected into this reply"
      >
        <BookOpen className="w-3 h-3 shrink-0 opacity-70" />
        <span className="truncate">
          <span className="text-[#4a4a4a]">Used </span>
          {summary}
          {more > 0 ? ` · +${more}` : ''}
        </span>
      </button>
      {open && (
        <ul className="mt-1.5 pl-4 space-y-1 border-l border-[#242424]">
          {receipts.map((r, i) => (
            <li key={`${r.kind}-${i}`} className="text-[11px] text-[#6a6a6a]">
              <span className="text-[#8a8884]">{formatReceipt(r)}</span>
              {r.detail && (
                <span className="text-[#4a4a4a]"> — {r.detail}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
